import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { LegalDocumentPDF } from '@/lib/pdf/LegalDocumentPDF';
import { buildProtectedPdf } from '@/lib/pdf/protected-pdf';
import { generateSignatureFieldMetadata } from '@/lib/pdf/signature-field-metadata';
import type { LegalDocument, SignatoryData } from '@/schemas/schema';
import { ensureAdditionalSignatoryArray } from '@/modules/templates/lib/signatory-fields';

// ---------------------------------------------------------------------------
// Signatory extraction (same priority chain as old Next.js route)
// ---------------------------------------------------------------------------

function extractSignatories(
  document: LegalDocument,
  formData: Record<string, unknown>
): SignatoryData[] {
  // Priority 1: document.signatories
  if (document.signatories?.length > 0) {
    return document.signatories.map((s) => ({
      party: s.party,
      name: s.name,
      email: s.email || '',
      role: s.title || s.party || 'Signatory',
      title: s.title,
      phone: s.phone,
      company: s.company,
      address: s.address,
    }));
  }

  const collected: SignatoryData[] = [];

  // Priority 2: formData.signatories array (new SignatoryScreen format)
  if (Array.isArray(formData.signatories) && formData.signatories.length > 0) {
    for (const entry of formData.signatories as Array<Record<string, unknown>>) {
      if (!entry.name || !entry.email) continue;
      collected.push({
        party: (entry.partyType as string) || 'other',
        name: entry.name as string,
        email: entry.email as string,
        role: (entry.title as string) || (entry.partyType as string) || 'Signatory',
        title: entry.title as string | undefined,
        phone: entry.phone as string | undefined,
        company: entry.company as string | undefined,
        address: entry.address as string | undefined,
      });
    }
    if (collected.length > 0) return collected;
  }

  // Priority 3: Form builder signatory fields
  if (formData.name && formData.email) {
    collected.push({
      party: (formData.party as string) || 'other',
      name: formData.name as string,
      email: formData.email as string,
      role: (formData.title as string) || (formData.party as string) || 'Signatory',
      title: formData.title as string | undefined,
      phone: formData.phone as string | undefined,
      company: formData.company as string | undefined,
      address: formData.address as string | undefined,
    });
    return collected;
  }

  // Priority 4: Numbered pattern (signatory_1_name, etc.)
  let idx = 1;
  while (formData[`signatory_${idx}_name`]) {
    collected.push({
      party: (formData[`signatory_${idx}_party`] as string) || 'other',
      name: formData[`signatory_${idx}_name`] as string,
      email: (formData[`signatory_${idx}_email`] as string) || '',
      role:
        (formData[`signatory_${idx}_title`] as string) ||
        (formData[`signatory_${idx}_party`] as string) ||
        'Signatory',
      title: formData[`signatory_${idx}_title`] as string | undefined,
      phone: formData[`signatory_${idx}_phone`] as string | undefined,
      company: formData[`signatory_${idx}_company`] as string | undefined,
      address: formData[`signatory_${idx}_address`] as string | undefined,
    });
    idx++;
  }
  if (collected.length > 0) return collected;

  // Priority 5: Additional signatories repeater (legacy)
  const additional = ensureAdditionalSignatoryArray(formData.additionalSignatories);
  for (const entry of additional) {
    if (!entry.name || !entry.email) continue;
    collected.push({
      party: entry.party || 'other',
      name: entry.name,
      email: entry.email,
      role: entry.title || entry.party || 'Signatory',
      title: entry.title,
      phone: entry.phone,
    });
  }
  if (collected.length > 0) return collected;

  // Priority 6: Legacy hardcoded field names
  if (formData.companyRepName || formData.companyRepEmail || formData.companyName) {
    collected.push({
      party: 'employer',
      name: String(formData.companyRepName || formData.companyName || 'Company'),
      email: String(formData.companyRepEmail || ''),
      role: String(formData.companyRepTitle || 'Authorized Representative'),
      phone: formData.companyRepPhone ? String(formData.companyRepPhone) : undefined,
    });
  }
  if (formData.employeeName || formData.employeeEmail) {
    collected.push({
      party: 'employee',
      name: String(formData.employeeName || 'Employee'),
      email: String(formData.employeeEmail || ''),
      role: String(formData.jobTitle || 'Employee'),
      phone: formData.employeePhone ? String(formData.employeePhone) : undefined,
    });
  }

  return collected;
}

// ---------------------------------------------------------------------------
// Generate PDF with metadata (replaces /api/documents/generate-pdf?metadata=true)
// ---------------------------------------------------------------------------

export interface PdfWithMetadata {
  success: boolean;
  pdfBlob: Blob;
  pdfBase64: string;
  signatureFields: ReturnType<typeof generateSignatureFieldMetadata>;
  signatories: SignatoryData[];
  metadata: {
    docId: string;
    pages: number;
  };
}

export async function generatePdfWithMetadata(
  document: LegalDocument,
  formData: Record<string, unknown>
): Promise<PdfWithMetadata> {
  const docId = `DOC-${Date.now().toString(36).toUpperCase().slice(-8)}`;
  const signatories = extractSignatories(document, formData);

  const documentForPdf = { ...document, signatories };

  const blob = await pdf(
    <LegalDocumentPDF document={documentForPdf} docId={docId} />
  ).toBlob();

  const contentBlockCount = document.content?.length || 0;
  const estimatedPages = Math.ceil(contentBlockCount / 2) + 2;

  const signatureFields = generateSignatureFieldMetadata(
    signatories.map((s) => ({ party: s.party, name: s.name, email: s.email || '' })),
    estimatedPages,
    document.letterhead
  );

  // Convert blob to base64
  const arrayBuffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
  const pdfBase64 = btoa(binary);

  return {
    success: true,
    pdfBlob: blob,
    pdfBase64,
    signatureFields,
    signatories,
    metadata: { docId, pages: estimatedPages },
  };
}

// ---------------------------------------------------------------------------
// Generate PDF for download (replaces /api/documents/generate-pdf)
// ---------------------------------------------------------------------------

/** Hand a generated blob to the browser as a file download. */
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = window.document.createElement('a');
  a.href = url;
  a.download = filename;
  window.document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  window.document.body.removeChild(a);
}

/** Render the document exactly as the normal download/preview path does. */
async function renderDocumentPdf(
  document: LegalDocument,
  formData: Record<string, unknown>
): Promise<Blob> {
  const docId = `DOC-${Date.now().toString(36).toUpperCase().slice(-8)}`;
  const signatories = extractSignatories(document, formData);
  const documentForPdf = { ...document, signatories };

  return pdf(<LegalDocumentPDF document={documentForPdf} docId={docId} />).toBlob();
}

export async function generatePdfForDownload(
  document: LegalDocument,
  formData: Record<string, unknown>,
  templateSlug: string
): Promise<void> {
  const blob = await renderDocumentPdf(document, formData);
  triggerDownload(blob, `${templateSlug}_${Date.now()}.pdf`);
}

// ---------------------------------------------------------------------------
// Protected download — an image-only copy with no extractable text
// ---------------------------------------------------------------------------

/**
 * Download a copy of the document that automated readers can't lift text from.
 *
 * Renders normally, then rasterises every page so the output carries no text
 * layer — `pdftotext`, PyPDF, pdf.js and the loaders behind most AI document
 * pipelines come back empty, and copy/paste yields nothing. A human still reads
 * and prints it normally.
 *
 * It does NOT stop a vision model that renders the pages as images; see
 * `@/lib/pdf/protected-pdf` for the measured reasoning and the trade-offs.
 */
export async function generateProtectedPdfForDownload(
  document: LegalDocument,
  formData: Record<string, unknown>,
  templateSlug: string
): Promise<void> {
  const source = await renderDocumentPdf(document, formData);
  const protectedBlob = await buildProtectedPdf(source);
  triggerDownload(protectedBlob, `${templateSlug}_protected_${Date.now()}.pdf`);
}
